/**
 * Today (the home page): what do I owe today.
 * - Meetings: the pre-call text before, Held / No-show / Rescheduled after, earlier no-shows;
 * - leads to answer (the 1-business-day clock), deals past their stage time, to-dos due;
 * - the show rate over the last 30 days.
 * "Mine" by default, or the whole team.
 */
import {
  useGetIdentity,
  useGetList,
  useNotify,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { Contact, Sale } from "../types";
import type { ZcrmAccount } from "../providers/zinspector/accounts";
import type { ZcrmActivity } from "../providers/zinspector/activities";
import { leadClock, type ZcrmLead } from "../providers/zinspector/leads";
import type { ZcrmDeal } from "../providers/zinspector/mappers";
import { toBoardDeal, OPEN_STAGES } from "../deals/pipeline";
import { leadTitle } from "../leads/leadDisplay";
import { useBookingSettings } from "../booking/useBookingSettings";
import { MeetingCard, type MeetingPerson } from "./MeetingCard";
import { earlierNoShows, meetingsToday, showRate, toDosDue } from "./today";

const ALL = { page: 1, perPage: 10_000 };
const OPEN_STAGE_IDS = new Set<string>(OPEN_STAGES.map((s) => s.id));

const Section = ({
  title,
  count,
  children,
  empty,
}: {
  title: string;
  count: number;
  children: ReactNode;
  empty: string;
}) => (
  <section aria-label={title} className="flex flex-col gap-2">
    <h2 className="flex items-center gap-2 font-semibold">
      {title}
      <span className="text-sm font-normal text-muted-foreground">{count}</span>
    </h2>
    {count === 0 ? (
      <p className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
        {empty}
      </p>
    ) : (
      children
    )}
  </section>
);

const personOf = (
  meeting: ZcrmActivity,
  contacts: Map<string, Contact>,
  accounts: Map<string, ZcrmAccount>,
): MeetingPerson => {
  const contact = meeting.contact_id
    ? contacts.get(String(meeting.contact_id))
    : undefined;
  const phones = contact?.phone_jsonb ?? [];
  return {
    name: contact ? `${contact.first_name} ${contact.last_name}`.trim() : "",
    first: contact?.first_name ?? "",
    phone:
      phones.find((p) => p.type !== "Work")?.number ?? phones[0]?.number ?? "",
    account: meeting.company_id
      ? (accounts.get(String(meeting.company_id))?.name ?? "")
      : "",
  };
};

export const TodayPage = ({ now }: { now?: Date; permissions?: unknown }) => {
  const { identity } = useGetIdentity();
  const [params, setParams] = useSearchParams();
  const everyone = params.get("owner") === "all";
  const ownerId: Identifier | null = everyone ? null : (identity?.id ?? null);
  const { settings } = useBookingSettings();

  const { data: activities = [] } = useGetList<ZcrmActivity>("activities", {
    pagination: ALL,
    sort: { field: "starts_at", order: "ASC" },
  });
  const { data: leads = [] } = useGetList<ZcrmLead>("leads", {
    pagination: ALL,
    sort: { field: "created_at", order: "DESC" },
  });
  const { data: deals = [] } = useGetList<ZcrmDeal>("deals", {
    pagination: ALL,
    sort: { field: "updated_at", order: "DESC" },
  });
  const { data: accountList = [] } = useGetList<ZcrmAccount>("companies", {
    pagination: ALL,
    sort: { field: "name", order: "ASC" },
  });
  const { data: contactList = [] } = useGetList<Contact>("contacts", {
    pagination: ALL,
    sort: { field: "last_name", order: "ASC" },
  });
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: ALL,
    sort: { field: "first_name", order: "ASC" },
  });

  const accounts = useMemo(
    () => new Map(accountList.map((a) => [String(a.id), a])),
    [accountList],
  );
  const contacts = useMemo(
    () => new Map(contactList.map((c) => [String(c.id), c])),
    [contactList],
  );
  const hostName = (id: Identifier | null) => {
    const s = sales.find((x) => String(x.id) === String(id));
    return s ? `${s.first_name} ${s.last_name}`.trim() : "";
  };
  const mine = (id: Identifier | null | undefined) =>
    ownerId == null || String(id) === String(ownerId);

  const meetings = meetingsToday(activities, ownerId, now);
  const todos = toDosDue(activities, ownerId, now);
  const rate = showRate(activities, ownerId, now);
  const leadsToAnswer = leads
    .filter(
      (l) =>
        l.status === "new" &&
        (everyone || !l.sales_id || mine(l.sales_id)) &&
        ["late", "soon"].includes(leadClock(l, now).kind),
    )
    .sort((a, b) => (a.reply_due ?? "").localeCompare(b.reply_due ?? ""));
  const stalled = deals
    .filter((d) => OPEN_STAGE_IDS.has(d.stage) && mine(d.sales_id))
    .map((d) => toBoardDeal(d, accounts.get(String(d.company_id)), now))
    .filter((d) => d.is_stalled)
    .sort((a, b) => b.days_in_stage - a.days_in_stage);

  const title = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(now ?? new Date());

  return (
    <div className="flex w-full max-w-5xl flex-col gap-6 pt-2">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Today · {title}</h1>
          <p className="text-sm text-muted-foreground">
            {meetings.length} meeting{meetings.length === 1 ? "" : "s"} ·{" "}
            {todos.length} to-do{todos.length === 1 ? "" : "s"} ·{" "}
            {leadsToAnswer.length} lead{leadsToAnswer.length === 1 ? "" : "s"}{" "}
            to answer
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {rate ? (
            <Badge
              variant={rate.rate >= 0.8 ? "success" : "warning"}
              title={`${rate.held} held, ${rate.noShows} no-shows`}
            >
              Show rate, last 30 days: {Math.round(rate.rate * 100)}%
            </Badge>
          ) : null}
          <div
            role="tablist"
            aria-label="Whose"
            className="inline-flex rounded-md border bg-background p-0.5"
          >
            {[
              { id: "me", label: "Mine" },
              { id: "all", label: "Team" },
            ].map((t) => {
              const selected = (t.id === "all") === everyone;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() =>
                    setParams(t.id === "all" ? { owner: "all" } : {}, {
                      replace: true,
                    })
                  }
                  className={
                    selected
                      ? "rounded bg-foreground px-3 py-1 text-sm font-medium text-background"
                      : "rounded px-3 py-1 text-sm text-muted-foreground"
                  }
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <Section
        title="Meetings"
        count={meetings.length}
        empty="No calls or meetings today."
      >
        <div className="flex flex-col gap-2">
          {meetings.map((m) => (
            <MeetingCard
              key={m.id}
              meeting={m}
              person={personOf(m, contacts, accounts)}
              hostName={hostName(m.sales_id)}
              brand={settings.brand.name}
              noShows={earlierNoShows(activities, m)}
              now={now}
            />
          ))}
        </div>
      </Section>

      <div className="grid gap-6 lg:grid-cols-3">
        <Section
          title="Leads to answer"
          count={leadsToAnswer.length}
          empty="Every new lead has its reply in time."
        >
          <ul className="flex flex-col gap-1.5">
            {leadsToAnswer.map((l) => {
              const clock = leadClock(l, now);
              return (
                <li key={l.id}>
                  <Link
                    to={`/leads?owner=all&lead=${l.id}`}
                    className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-accent/40"
                  >
                    <span className="truncate">{leadTitle(l)}</span>
                    <Badge
                      variant={clock.kind === "late" ? "error" : "warning"}
                    >
                      {clock.label}
                    </Badge>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Section>
        <Section
          title="Deals past their stage time"
          count={stalled.length}
          empty="No deal is past its stage time."
        >
          <ul className="flex flex-col gap-1.5">
            {stalled.map((d) => (
              <li key={d.id}>
                <Link
                  to={`/deals/${d.id}/show`}
                  className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-accent/40"
                >
                  <span className="truncate">{d.account_name}</span>
                  <Badge variant="error">
                    {d.days_in_stage}d of {d.limit_days}d
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
        <Section
          title="To-dos due"
          count={todos.length}
          empty="Nothing due today."
        >
          <ul className="flex flex-col gap-1.5">
            {todos.map((t) => (
              <ToDoRow
                key={t.id}
                todo={t}
                account={accounts.get(String(t.company_id))?.name}
              />
            ))}
          </ul>
        </Section>
      </div>
    </div>
  );
};

const ToDoRow = ({
  todo,
  account,
}: {
  todo: ZcrmActivity & { is_overdue: boolean };
  account?: string;
}) => {
  const [update, { isPending }] = useUpdate<ZcrmActivity>();
  const notify = useNotify();
  return (
    <li className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm">
      <span className="min-w-0">
        <span className="block truncate">{todo.title}</span>
        <span className="text-xs text-muted-foreground">
          {[account, todo.is_overdue ? "overdue" : "due today"]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={isPending}
        aria-label={`Done: ${todo.title}`}
        onClick={() =>
          update(
            "activities",
            { id: todo.id, data: { is_done: true }, previousData: todo },
            {
              mutationMode: "pessimistic",
              onSuccess: () => notify("Done", { type: "success" }),
            },
          )
        }
      >
        Done
      </Button>
    </li>
  );
};

export default TodayPage;
